<template>
  <InputOtp
    :disabled="disabled"
    :mask="mask"
    :name="name"
    :modelValue="modelValue"
    :data-test-id="`${name}-Otp`"
    :integerOnly="integerOnly"
    @update:modelValue="updateModelValue"
    @change="handleChange"
    @focus="handleFocus"
    @blur="handleBlur"
    v-bind="$attrs"
    class="w-full"
    :pt="getPassThroughProps()"
    :length="length"
    :class="[inputClasses]"
    :id="id"
  />
</template>

<script setup>
// Forward fallthrough attrs onto the inner control via v-bind="$attrs" rather
// than duplicating them onto the root, consistent with the other Base inputs.
defineOptions({ inheritAttrs: false });

import { computed, ref } from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  disabled: { type: Boolean, default: false },
  name: { type: String, default: "" },
  mask: { type: Boolean, default: false },
  integerOnly: { type: Boolean, default: false },
  length: { type: Number, default: 6 },
  invalid: Boolean,
  id: String,
  labelId: { type: String, default: null },
});

const emit = defineEmits(["update:modelValue", "change", "focus", "blur"]);
const focused = ref(false);
const updateModelValue = (value) => {
  emit("update:modelValue", value);
  emit("change", value);
};

const handleChange = (value) => emit("change", value);
const handleFocus = () => {
  focused.value = true;
};
const handleBlur = (value) => emit("blur", value);

const inputClasses = computed(() => ({
  "!border-deepSky": focused.value && !props.invalid,
  "!border-softGray": !focused.value && !props.invalid,
  "!border-error": props.invalid,
}));

const getPassThroughProps = () => {
  const baseClass =
    "!w-[32px] !p-[0px] !h-[40px] !rounded-[6px] !border-[1px] !bg-white flex items-center justify-center !text-[15px] md:!text-[24px] !leading-[1.5] !tracking-[0%] !font-400 min-[375px]:!w-[36px] min-[375px]:!h-[42px] min-[375px]:!text-[26px] sm:!w-[52px] sm:!h-[57px] sm:!text-[36.68px]";

  return {
    root: {
      class: "flex !gap-[4px] min-[375px]:!gap-[6px] sm:!gap-[16px] w-full",
    },
    pcInputText: {
      root: ({ index }) => {
        const val = String(props.modelValue).charAt(index);
        const hasValue = val && val.length > 0;

        return {
          class: `${baseClass} ${
            hasValue ? "!border-deepSky" : "!border-darkGray"
          } focus-within:!border-deepSky`,
          "aria-labelledby": props.labelId,
          "aria-required": true,
        };
      },
      input: {
        class: "text-center w-full h-full focus:outline-none",
      },
    },
  };
};
</script>
